// apps/vendor/src/features/print-queue/AssignmentDrawer.tsx
//
// R3-8 (2026-05-16) — right-side drawer showing full assignment / order
// details from /print-queue. Driven by useOrderDetail to keep one source
// of truth with the order detail page.

import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@printway/ui';

import { ProductionHistoryPanel } from '../orders/detail/ProductionHistoryPanel.js';
import {
  useOrderDetail,
  useProductionHistory,
  useTransitionItem,
  type OrderStatus,
} from '../orders/api.js';
import type { PrintQueueAssignment } from './api.js';

interface Props {
  open: boolean;
  assignment: PrintQueueAssignment | null;
  onClose: () => void;
}

const STATUS_LABEL_KEY: Record<OrderStatus, string> = {
  assigned: 'statusLabel.assigned',
  accepted: 'statusLabel.accepted',
  in_production: 'statusLabel.inProduction',
  quality_check: 'statusLabel.qualityCheck',
  ready_to_ship: 'statusLabel.readyToShip',
  shipped: 'statusLabel.shipped',
  pending_reassignment: 'statusLabel.pendingReassignment',
  completed: 'statusLabel.completed',
};

// R11 (2026-05-17) — only transitions that don't need extra metadata
// are fired inline from the drawer. `accepted → in_production`
// (DimInputModal), `quality_check → ready_to_ship` (QCModal),
// and `ready_to_ship → shipped` (TrackingInputModal) live on the
// canonical detail page's ActionBar; the drawer routes the user
// there instead of 422-ing.
const NEXT_STATE: Partial<Record<OrderStatus, OrderStatus>> = {
  assigned: 'accepted',
  in_production: 'quality_check',
  shipped: 'completed',
};

function fmtTs(ts: number | undefined, locale: string): string {
  if (!ts || ts <= 0) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const langTag = locale === 'vi' ? 'vi-VN' : locale === 'fr' ? 'fr-FR' : 'en-US';
  return d.toLocaleString(langTag, { hour12: false });
}

export function AssignmentDrawer({
  open,
  assignment,
  onClose,
}: Props): JSX.Element | null {
  const { t, i18n } = useTranslation('orders');
  const orderId = assignment?.order_id;
  const itemId = assignment?.item_id;
  const detailQ = useOrderDetail(orderId, itemId);
  const historyQ = useProductionHistory(orderId, itemId);
  const transitionM = useTransitionItem();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !assignment) return null;

  const detail = detailQ.data;
  const status = (detail?.status ?? assignment.item_status ?? 'assigned') as OrderStatus;
  const nextState = NEXT_STATE[status];
  const itemVersion = detail?.item_version ?? assignment.item_version;
  const canTransition =
    nextState &&
    typeof orderId === 'string' &&
    typeof itemId === 'string' &&
    typeof itemVersion === 'number';

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="print-queue-drawer"
      onClick={onClose}
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-40 flex justify-end bg-black/30"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-pw-bg shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-pw-border px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-pw-text">
              {detailQ.isLoading
                ? t('printQueue.drawer.loadingOrder')
                : detail?.order_summary?.order_number ??
                  `#${(orderId ?? '').slice(-8)}`}
            </h2>
            <p className="text-xs text-pw-text-muted">
              Item {(itemId ?? '').slice(-8)} · v{itemVersion ?? '?'}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            aria-label={t('printQueue.drawer.closeAria')}
            className="h-auto rounded-md border border-pw-border bg-pw-surface px-3 py-1 text-xs font-medium text-pw-text hover:bg-pw-bg"
          >
            {t('printQueue.drawer.closeButton')}
          </Button>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4 text-sm">
          <section className="rounded-md border border-pw-border bg-pw-bg p-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-pw-text-muted">
              {t('printQueue.drawer.sectionStatus')}
            </h3>
            <p className="text-sm text-pw-text">{t(STATUS_LABEL_KEY[status])}</p>
            {nextState && canTransition ? (
              <Button
                type="button"
                data-testid="print-queue-drawer-next"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  setErr(null);
                  transitionM.mutate(
                    {
                      order_id: orderId!,
                      item_id: itemId!,
                      item_version: itemVersion!,
                      to_state: nextState,
                    },
                    {
                      onSuccess: () => {
                        setBusy(false);
                        onClose();
                      },
                      onError: (e) => {
                        setBusy(false);
                        setErr(
                          e.userMessage ??
                            e.message ??
                            t('printQueue.drawer.errorTransition'),
                        );
                      },
                    },
                  );
                }}
                className="mt-2 h-auto w-full rounded-md bg-pw-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-pw-primary-strong disabled:opacity-60"
              >
                {busy ? t('printQueue.drawer.updating') : `→ ${t(STATUS_LABEL_KEY[nextState])}`}
              </Button>
            ) : null}
            {err ? (
              <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700">
                {err}
              </p>
            ) : null}
            {/* R11 — for states that need a modal (accepted, quality_check,
                ready_to_ship) we point the vendor to the detail page where
                DimInputModal / QCModal / TrackingInputModal collect input.
                The footer Link already covers that, this hint just makes
                the missing-button case explicit instead of looking broken. */}
            {!nextState && status !== 'completed' && status !== 'pending_reassignment' ? (
              <p className="mt-2 text-[11px] text-pw-text-muted">
                {t('printQueue.drawer.needsModal')}
              </p>
            ) : null}
            {/* R6-1 (2026-05-16) — reject button moved to /orders detail
                ActionBar so the vendor confirms in the canonical detail view
                instead of the inline drawer. */}
          </section>

          {detail?.order_summary ? (
            <section className="rounded-md border border-pw-border bg-pw-bg p-3">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-pw-text-muted">
                {t('printQueue.drawer.sectionOrderSummary')}
              </h3>
              <dl className="grid grid-cols-2 gap-y-1 text-xs">
                <dt className="text-pw-text-muted">{t('printQueue.drawer.labelOrderCode')}</dt>
                <dd className="text-pw-text">
                  {detail.order_summary.order_number ?? '—'}
                </dd>
                <dt className="text-pw-text-muted">{t('printQueue.drawer.labelQuantity')}</dt>
                <dd className="text-pw-text">
                  {typeof detail.order_summary.quantity === 'number'
                    ? detail.order_summary.quantity
                    : '—'}
                </dd>
                <dt className="text-pw-text-muted">{t('printQueue.drawer.labelRecipient')}</dt>
                <dd className="text-pw-text">
                  {detail.order_summary.recipient_name ?? '—'}
                </dd>
                {detail.order_summary.ship_to ? (
                  <>
                    <dt className="text-pw-text-muted">{t('printQueue.drawer.labelAddress')}</dt>
                    <dd className="text-pw-text">
                      {[
                        detail.order_summary.ship_to.line1,
                        detail.order_summary.ship_to.line2,
                        detail.order_summary.ship_to.city,
                        detail.order_summary.ship_to.state,
                        detail.order_summary.ship_to.postal,
                        detail.order_summary.ship_to.country,
                      ]
                        .filter((s) => typeof s === 'string' && s.length > 0)
                        .join(', ') || '—'}
                    </dd>
                  </>
                ) : null}
              </dl>
            </section>
          ) : null}

          <section className="rounded-md border border-pw-border bg-pw-bg p-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-pw-text-muted">
              {t('printQueue.drawer.sectionSla')}
            </h3>
            <p className="text-xs text-pw-text">
              {t('printQueue.drawer.slaDeadline', {
                value: fmtTs(detail?.effective_deadline ?? assignment.sla_deadline, i18n.language),
              })}
            </p>
            <p className="text-xs text-pw-text">
              {t('printQueue.drawer.slaAssignedAt', {
                value: fmtTs(assignment.assigned_at, i18n.language),
              })}
            </p>
          </section>

          <ProductionHistoryPanel
            rows={historyQ.data?.rows ?? []}
            isLoading={historyQ.isLoading}
          />

          {detailQ.error ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {t('printQueue.drawer.detailError', { message: detailQ.error.message })}
            </p>
          ) : null}
        </div>

        <footer className="border-t border-pw-border bg-pw-surface px-4 py-3">
          {orderId && itemId ? (
            <Link
              to={`/orders/${orderId}/items/${itemId}`}
              onClick={onClose}
              className="block w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-center text-xs font-medium text-pw-primary hover:bg-pw-surface"
            >
              {t('printQueue.drawer.openDetail')}
            </Link>
          ) : null}
        </footer>
      </aside>
    </div>
  );
}

export default AssignmentDrawer;
